import { MenuData } from '$core/models/static';
import { LanguageService } from '$core/services/language.service';
import { ResizeService } from '$core/services/resize.service';
import { HeaderService, StaticStorage } from '$core/services/static';
import {
  createFailureState,
  createIdleState,
  createSuccessState,
} from '$core/util/state-management/request-state';
import { createLoadingState, RequestState } from '$core/util';
import {
  animateChild,
  query,
  stagger,
  transition,
  trigger,
  useAnimation,
} from '@angular/animations';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { fadeInUp } from 'ng-animate';
import { NgScrollbar } from 'ngx-scrollbar';
import { startWith, Subscription } from 'rxjs';
import { ContentUnavailableStyleEnum } from '../content-unavailable/content-unavailable.component';

@Component({
  selector: 'app-menu-items',
  templateUrl: './menu-items.component.html',
  styleUrls: ['../../../../assets/scss/modules/_menu-items.component.scss'],
  animations: [
    trigger('fadeInUpChild', [
      transition('void => *', [
        useAnimation(fadeInUp, {
          params: { timing: 0.2, a: '10px' },
        }),
      ]),
    ]),
    trigger('fadeInUp', [
      transition('void => *', [
        useAnimation(fadeInUp, { params: { timing: 0.2, a: '-10px' } }),
        query('@fadeInUpChild', stagger('40ms', [animateChild()]), {
          optional: true,
        }),
      ]),
    ]),
  ],
})
export class MenuItemsComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly resizeService = inject(ResizeService);
  private readonly headerService = inject(HeaderService);
  private readonly staticStorage = inject(StaticStorage);
  private readonly destroyRef = inject(DestroyRef);

  @Input() public ps: NgScrollbar;
  public readonly isEyeServiceEnabled = input.required<boolean>();

  public readonly faChevronDown = faChevronDown;
  public readonly contentUnavailableStyleEnum = ContentUnavailableStyleEnum;

  public activeIndex: string;

  protected readonly staticRequestState =
    signal<RequestState<MenuData>>(createIdleState());

  private loadSubscription?: Subscription;

  protected readonly data = computed(() => this.staticStorage.static().menu);

  public get isMobile() {
    return this.resizeService.isMobile;
  }

  public ngOnInit(): void {
    this.staticStorage.langChange$
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.staticStorage.hasActual('menu')) {
          this.loadData();
        }
      });
  }

  protected reload(): void {
    this.loadData();
  }

  // Animation do not works when use localize pipe, use this method
  public localizePath(path: string) {
    return this.languageService.translateRoute(path);
  }

  public onClick(i: number, j: number) {
    if (!this.ps) {
      return;
    }
    const scroll = () => {
      const element = this.ps.viewport.nativeElement.querySelector(
        '.menu-items-group--active'
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    const ij = this.createCombiIndex(i, j);

    if (this.activeIndex === ij) {
      this.activeIndex = null;
      return;
    }

    this.activeIndex = ij;

    setTimeout(() => {
      scroll();
    }, 100);
  }

  public createCombiIndex(i: number, j: number) {
    return i.toString() + j.toString();
  }

  private loadData(): void {
    this.loadSubscription?.unsubscribe();
    this.staticRequestState.set(createLoadingState());
    this.loadSubscription = this.headerService
      .getHeaderMenuStatics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: MenuData) =>
          this.staticRequestState.set(createSuccessState(data)),
        error: (error) =>
          this.staticRequestState.set(createFailureState(error)),
      });
  }
}
